<template>
  <main class="pixso-page air-ground-page" :class="{ 'air-ground-page--en': isEnglish }">
    <section class="air-ground-page__hero" :style="{ backgroundImage: `url(${heroImage})` }"
      aria-labelledby="air-ground-title">
      <div class="air-ground-page__hero-copy">
        <h1 id="air-ground-title" class="air-ground-page__hero-title">
          {{ t('AirGroundLogistics.hero.title') }}
        </h1>
      </div>
    </section>

    <section class="air-ground-page__overview" aria-labelledby="air-ground-overview-title">
      <p class="air-ground-page__eyebrow">{{ t('AirGroundLogistics.overview.eyebrow') }}</p>
      <h2 id="air-ground-overview-title" class="air-ground-page__section-title">
        {{ t('AirGroundLogistics.overview.title') }}
      </h2>
      <p v-if="t('AirGroundLogistics.overview.description')" class="air-ground-page__overview-desc">
        {{ t('AirGroundLogistics.overview.description') }}
      </p>
      <figure class="air-ground-page__media air-ground-page__media--video">
        <button
          v-if="!videoPlaying"
          type="button"
          class="air-ground-page__video-cover"
          :aria-label="t('AirGroundLogistics.overview.videoPlayLabel') || '播放空地协同物流配送演示视频'"
          @click="startVideo"
        >
          <img
            class="air-ground-page__video-poster"
            :src="airGroundVideoPoster"
            :alt="t('AirGroundLogistics.overview.videoPlayLabel') || '播放空地协同物流配送演示视频'"
          />
          <img
            class="air-ground-page__video-play"
            :src="videoPlayIcon"
            alt=""
            aria-hidden="true"
          />
        </button>
        <video
          ref="videoRef"
          v-show="videoPlaying"
          :src="airGroundVideo"
          :aria-label="t('AirGroundLogistics.overview.videoPlayLabel') || '播放空地协同物流配送演示视频'"
          controls
          playsinline
          preload="metadata"
        />
      </figure>
    </section>

    <section ref="timelineRootRef" class="air-ground-page__timeline"
      :aria-label="t('AirGroundLogistics.timeline.ariaLabel')">
      <article v-for="(step, index) in timelineSteps" :key="step.key" :ref="(el) => setTimelineStepRef(el, index)"
        class="air-ground-page__step">
        <div class="air-ground-page__step-media">
          <div class="air-ground-page__step-stage" :ref="(el) => setStepStageRef(el, step.key)">
            <div class="air-ground-page__step-mobile-heading">
              <span class="air-ground-page__step-mobile-index">{{ `${step.mobileStage + 1}.` }}</span>
              <span class="air-ground-page__step-mobile-title">{{ step.mobileHeading }}</span>
            </div>
            <img class="air-ground-page__step-image" :src="step.image" :alt="step.alt" loading="lazy"
              @load="setStepImageSize($event, step.key)" />
            <div class="air-ground-page__step-mask" aria-hidden="true"></div>
            <div v-if="step.hotspots.length" class="air-ground-page__step-hotspots">
              <div v-for="hotspot in step.hotspots" :key="hotspot.id" class="air-ground-page__hotspot"
                :class="{ 'is-active': activeHotspotIds.includes(hotspot.id) }" :style="getHotspotStyle(step.key, hotspot)"
                @mouseenter="setActiveHotspot(hotspot.id)" @mouseleave="clearActiveHotspot(hotspot.id)"
                @focusin="setActiveHotspot(hotspot.id)" @focusout="clearActiveHotspot(hotspot.id)">
                <div
                  class="air-ground-page__hotspot-bubble"
                  :style="getBubbleStyle(step.key, hotspot)"
                >
                  <div class="air-ground-page__hotspot-bubble-text">
                    {{ hotspot.title }}
                  </div>
                </div>
                <span class="air-ground-page__hotspot-pulse" aria-hidden="true"></span>
                <button type="button" class="air-ground-page__hotspot-trigger" :aria-label="hotspot.title"></button>
                <span class="air-ground-page__hotspot-core" aria-hidden="true"></span>
              </div>
              <template v-for="hotspot in step.hotspots" :key="`${hotspot.id}-preview`">
                <div v-if="hotspot.previewImage" class="air-ground-page__hotspot-preview"
                  :class="{ 'is-active': activeHotspotIds.includes(hotspot.id) }" :style="{
                    right: `${hotspot.previewRight}px`,
                    top: `${hotspot.previewTop}px`,
                    width: `${hotspot.previewWidth}px`
                  }">
                  <img class="air-ground-page__hotspot-preview-image" :src="hotspot.previewImage"
                    :alt="hotspot.previewAlt || hotspot.title" loading="lazy" />
                </div>
              </template>
            </div>
          </div>
        </div>
      </article>
      <div ref="workflowShellRef" class="air-ground-page__workflow-shell" :class="{ 'is-visible': isWorkflowVisible }">
        <ol
          class="air-ground-page__workflow"
          :aria-label="t('AirGroundLogistics.workflow.ariaLabel')"
          :style="{ '--air-ground-workflow-progress': workflowProgress, '--air-ground-workflow-count': workflowItems.length }"
        >
          <li v-for="(item, labelIndex) in workflowItems" :key="item.label" class="air-ground-page__workflow-item"
            :class="{
              'is-done': labelIndex < activeWorkflowStage,
              'is-active': labelIndex === activeWorkflowStage
            }">
            <button
              type="button"
              class="air-ground-page__workflow-content"
              :aria-current="labelIndex === activeWorkflowStage ? 'step' : undefined"
              @click="scrollToWorkflowStage(labelIndex)">
              <span class="air-ground-page__workflow-dot"
                :style="{ '--air-ground-workflow-icon': `url(${item.icon})` }" aria-hidden="true">
                <span class="air-ground-page__workflow-icon"></span>
              </span>
              <span class="air-ground-page__workflow-label">{{ item.label }}</span>
            </button>
          </li>
        </ol>
      </div>
    </section>
  </main>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import sceneDeliveryImage from '@/assets/images/pixso-air-ground-solution/official-step-vehicle-return.jpg'
import sceneElevatorImage from '@/assets/images/pixso-air-ground-solution/official-step-5-7.jpg'
import sceneFlightImage from '@/assets/images/pixso-air-ground-solution/official-step-flight.jpg'
import sceneFrontdeskImage from '@/assets/images/pixso-air-ground-solution/official-step-6.jpg'
import sceneLandingImage from '@/assets/images/pixso-air-ground-solution/official-step-3.jpg'
import sceneReturnImage from '@/assets/images/pixso-air-ground-solution/official-step-5-3.jpg'
import sceneTakeoffImage from '@/assets/images/pixso-air-ground-solution/official-step-1.jpg'
import sceneUnloadImage from '@/assets/images/pixso-air-ground-solution/official-step-5-5.jpg'
import sceneVehicleImage from '@/assets/images/pixso-air-ground-solution/official-step-vehicle-return.jpg'
import heroImage from '@/assets/images/pixso-air-ground-solution/hero.jpg'
import airGroundVideoZh from '@/assets/videos/pixso-air-ground-solution/full-space-logistics.mp4'
import airGroundVideoEn from '@/assets/i18n/en/solution/air-ground/full-space-logistics.mp4'
import airGroundVideoPosterZh from '@/assets/images/pixso-air-ground-solution/full-space-logistics-first-frame.jpg'
import airGroundVideoPosterEn from '@/assets/i18n/en/solution/air-ground/full-space-logistics-first-frame.jpg'
import videoPlayIcon from '@/assets/images/pixso-air-ground-solution/video-play.svg'
import workflowStep1Icon from '@/assets/images/step/1.svg'
import workflowStep2Icon from '@/assets/images/step/2.svg'
import workflowStep3Icon from '@/assets/images/step/3.svg'
import workflowStep4Icon from '@/assets/images/step/4.svg'
import workflowStep5Icon from '@/assets/images/step/5.svg'
import workflowStep6Icon from '@/assets/images/step/6.svg'
import workflowStep7Icon from '@/assets/images/step/7.svg'
import workflowStep8Icon from '@/assets/images/step/8.svg'
import firstPagePreviewImage from '@/assets/images/step/first-page.png'

const { t, tm, locale } = useI18n()
const timelineRootRef = ref(null)
const workflowShellRef = ref(null)
const activeWorkflowStage = ref(0)
const activeTimelineStepIndex = ref(-1)
const isWorkflowVisible = ref(false)
const activeHotspotIds = ref([])
const workflowProgress = ref(0)
const lastWindowScrollY = ref(0)
const videoPlaying = ref(false)
const videoRef = ref(null)
const isPortraitMobile = ref(false)
const isEnglish = computed(() => locale.value === 'en')

const videoTeaserTexts = computed(() => {
  if (isEnglish.value) {
    return {
      slogan: 'Smart Architect of Full-Space Unmanned System Scenarios',
      subtitle: 'Logistics: Low-Altitude Economy Community Smart Delivery'
    }
  }
  return {
    slogan: '全空间无人体系场景智造者',
    subtitle: '物流篇之低空经济社区智能配送方案'
  }
})
const airGroundVideo = computed(() => (isEnglish.value ? airGroundVideoEn : airGroundVideoZh))
const airGroundVideoPoster = computed(() => (isEnglish.value ? airGroundVideoPosterEn : airGroundVideoPosterZh))
const timelineStepRefs = []
const stepStageRefs = {}
const stepImageSizeMap = ref({})
const stepStageViewportMap = ref({})
const HOTSPOT_BUBBLE_DEFAULT_LEFT = 60
const HOTSPOT_BUBBLE_DEFAULT_TOP = 0
const HOTSPOT_BUBBLE_DEFAULT_RADIUS = 24
const HOTSPOT_BUBBLE_STAGE_PAD = 12
const HOTSPOT_BUBBLE_MAX_WIDTH = 280
const WORKFLOW_HIDE_EPSILON = 2
const AUTO_HOTSPOT_DELAY = 700
const AUTO_HOTSPOT_INTERVAL = 900
let autoHotspotTimer = null
let autoHotspotInterval = null

const updateIsPortraitMobile = () => {
  if (typeof window === 'undefined') return
  isPortraitMobile.value = window.matchMedia('(max-width: 1200px) and (orientation: portrait)').matches
}

const hotspotLayoutMap = {
  takeoff: [
    {
      slot: 'default',
      x: 24.7,
      y: 51,
      bubbleLeft: 36,
      bubbleTop: -23,
      bubbleOpen: 'right',
    },
    {
      slot: 'default',
      x: 57.2,
      y: 40,
      // 手机端上移，避免与「无人机起飞」气泡重叠
      mobileY: 30,
      mobileBubbleTop: -40,
      bubbleLeft: 36,
      bubbleTop: -23,
      bubbleOpen: 'right',
      previewImage: firstPagePreviewImage,
      previewAltKey: 'AirGroundLogistics.timeline.hotspots.mobileOrderPageAlt',
      previewRight: 24,
      previewTop: 88,
      previewWidth: 260,
      titleKey: 'AirGroundLogistics.timeline.hotspots.mobileOrder',
    }
  ],
  flight: [{
    slot: 'default', x: 37.7, y: 38.8, bubbleLeft: 36,
    bubbleTop: -23,
    bubbleOpen: 'right',
  }],
  landing: [{
    slot: 'default', x: 50, y: 31, bubbleLeft: 36,
    bubbleTop: -23,
    bubbleOpen: 'right',
  },{
    slot: 'default', x: 55, y: 60, bubbleLeft: 36,
    bubbleTop: -23,
    bubbleOpen: 'right',
    titleKey: 'AirGroundLogistics.timeline.hotspots.cabinetUnload',
  }],
  vehicle: [
    {
      slot: 'default', x: 38.4, y: 62.6, bubbleLeft: 36,
      bubbleTop: -23,
      bubbleOpen: 'right',
    },
    {
      slot: 'secondary', x: 50.2, y: 16.9, bubbleLeft: 36,
      bubbleTop: -23,
      bubbleOpen: 'right',
      titleKey: 'AirGroundLogistics.timeline.hotspots.uavReturn',
    }
  ],
  elevator: [{
    slot: 'default', x: 60, y: 50.6, bubbleLeft: 36,
    bubbleTop: -23,
    bubbleOpen: 'right',
  }],
  unload: [{
    slot: 'default', x: 42.9, y: 55.5, bubbleLeft: 36,
    bubbleTop: -23,
    bubbleOpen: 'right',
  }],
  frontdesk: [{
    // 无人车卸货：贴近车身右侧，朝右展开
    slot: 'default',
    x: 57.2,
    y: 55.8,
    mobileX: 50,
    mobileY: 46,
    mobileBubbleTop: -30,
    bubbleLeft: 36,
    bubbleTop: -23,
    bubbleOpen: 'right',
    titleKey: 'AirGroundLogistics.timeline.hotspots.vehicleUnload',
  },{
    // 前台包裹：绿点贴包裹不动，仅气泡略下移、朝右展开
    slot: 'default',
    x: 29.2,
    y: 72,
    mobileX: 30,
    mobileY: 66,
    mobileBubbleTop: -14,
    bubbleLeft: 36,
    bubbleTop: -23,
    bubbleOpen: 'right',
  }],
  return: [{
    slot: 'default', x: 52.4, y: 53.9, bubbleLeft: 36,
    bubbleTop: -23,
    bubbleOpen: 'right',
  }]
}
const timelineImages = {
  takeoff: sceneTakeoffImage,
  flight: sceneFlightImage,
  landing: sceneLandingImage,
  vehicle: sceneVehicleImage,
  delivery: sceneDeliveryImage,
  elevator: sceneElevatorImage,
  unload: sceneUnloadImage,
  frontdesk: sceneFrontdeskImage,
  return: sceneReturnImage
}

const workflowIcons = [
  workflowStep1Icon,
  workflowStep2Icon,
  workflowStep3Icon,
  workflowStep4Icon,
  workflowStep5Icon,
  workflowStep8Icon,
  workflowStep6Icon,
  workflowStep7Icon
]

const workflowItems = computed(() => {
  const labels = tm('AirGroundLogistics.workflow.labels') || []
  return labels.map((label, index) => ({
    label,
    icon: workflowIcons[index] || workflowIcons[workflowIcons.length - 1]
  }))
})

const workflowTargetStepKeys = [
  'takeoff',
  'flight',
  'landing',
  'vehicle',
  'unload',
  'elevator',
  'frontdesk',
  'return'
]

const workflowStepIndexes = computed(() => workflowItems.value.map((_, workflowIndex) => {
  const targetStepKey = workflowTargetStepKeys[workflowIndex]
  const targetIndex = timelineSteps.value.findIndex((step) => step.key === targetStepKey)

  return targetIndex >= 0 ? targetIndex : Math.min(workflowIndex, Math.max(timelineSteps.value.length - 1, 0))
}))

const createHotspot = (step, layout, index) => {
  const hotspotTitle = layout.titleKey ? t(layout.titleKey) : (layout.title || step.title)
  const hotspotPreviewAlt = layout.previewAltKey ? t(layout.previewAltKey) : (layout.previewAlt || '')

  return {
    id: `${step.key}-${layout.slot || 'hotspot'}-${index}`,
    x: isPortraitMobile.value && layout.mobileX != null ? layout.mobileX : layout.x,
    y: isPortraitMobile.value && layout.mobileY != null ? layout.mobileY : layout.y,
    title: hotspotTitle,
    bubbleLeft: isPortraitMobile.value ? 16 : (layout.bubbleLeft ?? HOTSPOT_BUBBLE_DEFAULT_LEFT),
    bubbleTop: isPortraitMobile.value && layout.mobileBubbleTop != null
      ? layout.mobileBubbleTop
      : (layout.bubbleTop ?? HOTSPOT_BUBBLE_DEFAULT_TOP),
    bubbleRadius: layout.bubbleRadius ?? HOTSPOT_BUBBLE_DEFAULT_RADIUS,
    // 默认优先朝右；仅当右侧空间明显不足时回退朝左
    bubbleOpen: layout.bubbleOpen || 'right',
    previewImage: layout.previewImage ?? '',
    previewAlt: hotspotPreviewAlt,
    previewRight: layout.previewRight ?? 24,
    previewTop: layout.previewTop ?? 0,
    previewWidth: layout.previewWidth ?? 240
  }
}

const timelineStages = {
  takeoff: 0,
  flight: 1,
  landing: 2,
  vehicle: 3,
  elevator: 5,
  unload: 4,
  frontdesk: 6,
  return: 7
}

const timelineSteps = computed(() => {
  const steps = tm('AirGroundLogistics.timeline.steps') || []
  return steps.map((step) => ({
    ...step,
    image: timelineImages[step.key],
    stage: timelineStages[step.key] ?? 0,
    mobileStage: timelineStages[step.key] ?? 0,
    mobileHeading: workflowItems.value[timelineStages[step.key] ?? 0]?.label || step.title,
    hotspots: (hotspotLayoutMap[step.key] || []).map((layout, index) => createHotspot(step, layout, index))
  }))
})


const setTimelineStepRef = (element, index) => {
  if (element) {
    timelineStepRefs[index] = element
    return
  }

  delete timelineStepRefs[index]
}

const setStepStageRef = (element, stepKey) => {
  if (element) {
    stepStageRefs[stepKey] = element
    return
  }

  delete stepStageRefs[stepKey]
  const nextViewportMap = { ...stepStageViewportMap.value }
  if (nextViewportMap[stepKey]) {
    delete nextViewportMap[stepKey]
    stepStageViewportMap.value = nextViewportMap
  }
}

const getStickyOffset = () => {
  const navHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--pixso-nav-height')) || 72
  return navHeight + 48
}

const getWorkflowClearance = () => {
  const shell = workflowShellRef.value
  if (!shell) return 0

  const rect = shell.getBoundingClientRect()
  const styles = getComputedStyle(shell)
  const bottom = parseFloat(styles.bottom) || 0
  return rect.height + bottom
}

const getVisibleFooterRect = () => {
  const footerElements = Array.from(document.querySelectorAll('footer'))
  for (const footerElement of footerElements) {
    const rect = footerElement.getBoundingClientRect()
    if (rect.width > 0 && rect.height > 0) {
      return rect
    }
  }

  return null
}

const clamp = (value, min, max) => Math.min(Math.max(value, min), max)

const updateWorkflowProgress = () => {
  const targetIndexes = workflowStepIndexes.value
  const targetElements = targetIndexes
    .map((stepIndex) => timelineStepRefs[stepIndex])
    .filter(Boolean)

  if (targetElements.length <= 1) {
    workflowProgress.value = 0
    return
  }

  const currentY = (window.scrollY || window.pageYOffset || 0) + 1
  const targetYs = targetElements.map((element) => {
    const rect = element.getBoundingClientRect()
    return (window.scrollY || window.pageYOffset || 0) + rect.top
  })

  if (currentY <= targetYs[0]) {
    workflowProgress.value = 0
    return
  }

  const lastTargetIndex = targetYs.length - 1
  if (currentY >= targetYs[lastTargetIndex]) {
    workflowProgress.value = 1
    return
  }

  const segmentIndex = targetYs.findIndex((targetY, index) => (
    index < lastTargetIndex && currentY >= targetY && currentY < targetYs[index + 1]
  ))
  const safeSegmentIndex = Math.max(segmentIndex, 0)
  const segmentStart = targetYs[safeSegmentIndex]
  const segmentEnd = targetYs[safeSegmentIndex + 1]
  const segmentProgress = segmentEnd === segmentStart
    ? 0
    : clamp((currentY - segmentStart) / (segmentEnd - segmentStart), 0, 1)

  workflowProgress.value = (safeSegmentIndex + segmentProgress) / lastTargetIndex
}

const updateActiveWorkflowStage = () => {
  const root = timelineRootRef.value
  const stepElements = timelineStepRefs.filter(Boolean)
  if (!root || timelineSteps.value.length === 0 || stepElements.length === 0) return

  const previousActiveIndex = activeTimelineStepIndex.value
  const wasWorkflowVisible = isWorkflowVisible.value
  const currentScrollY = window.scrollY || window.pageYOffset || 0
  const rootRect = root.getBoundingClientRect()
  
  // 只有当 timeline 完全滚出视口下方时才彻底隐藏
  if (rootRect.bottom <= 0) {
    activeTimelineStepIndex.value = -1
    isWorkflowVisible.value = false
    workflowProgress.value = 0
    lastWindowScrollY.value = currentScrollY
    if (previousActiveIndex !== -1) {
      scheduleAutoHotspots()
    }
    return
  }

  const shell = workflowShellRef.value
  const shellRect = shell?.getBoundingClientRect()
  const footerRect = getVisibleFooterRect()
  const hasFooterOverlap = Boolean(
    shellRect
    && footerRect
    && footerRect.top <= shellRect.bottom + WORKFLOW_HIDE_EPSILON
  )

  const triggerY = 1
  let activeIndex = 0

  stepElements.forEach((stepElement, index) => {
    const rect = stepElement.getBoundingClientRect()
    if (rect.top <= triggerY) {
      activeIndex = index
    }
  })

  const firstStepRect = stepElements[0]?.getBoundingClientRect()
  const hasMovedAboveFirstStepShowRange = Boolean(
    firstStepRect && firstStepRect.top >= firstStepRect.height / 3
  )
  const shouldHideBeforeFirstStep = activeIndex === 0 && hasMovedAboveFirstStepShowRange

  isWorkflowVisible.value = (
    rootRect.top < window.innerHeight
    && !hasFooterOverlap
    && !shouldHideBeforeFirstStep
  )

  activeTimelineStepIndex.value = activeIndex
  activeWorkflowStage.value = timelineSteps.value[activeIndex]?.stage ?? 0
  updateWorkflowProgress()
  lastWindowScrollY.value = currentScrollY

  if (previousActiveIndex !== activeIndex || (!wasWorkflowVisible && isWorkflowVisible.value)) {
    scheduleAutoHotspots()
  }
}

const scrollToWorkflowStage = (workflowIndex) => {
  const targetIndex = workflowStepIndexes.value[workflowIndex]
  const stepElement = timelineStepRefs[targetIndex]
  if (!stepElement) return

  activeTimelineStepIndex.value = targetIndex
  activeWorkflowStage.value = workflowIndex
  activeHotspotIds.value = []
  scheduleAutoHotspots()

  stepElement.scrollIntoView({
    block: 'start',
    behavior: 'smooth'
  })
}

const setActiveHotspot = (hotspotId) => {
  clearAutoHotspots()
  activeHotspotIds.value = [hotspotId]
}

const clearActiveHotspot = (hotspotId) => {
  if (activeHotspotIds.value.includes(hotspotId)) {
    activeHotspotIds.value = []
  }
  scheduleAutoHotspots()
}

const clearAutoHotspots = () => {
  if (autoHotspotTimer) {
    window.clearTimeout(autoHotspotTimer)
    autoHotspotTimer = null
  }

  if (autoHotspotInterval) {
    window.clearInterval(autoHotspotInterval)
    autoHotspotInterval = null
  }
}

const scheduleAutoHotspots = () => {
  clearAutoHotspots()
  activeHotspotIds.value = []

  if (!isWorkflowVisible.value) {
    return
  }

  const activeStep = timelineSteps.value[activeTimelineStepIndex.value]
  const hotspots = activeStep?.hotspots || []
  if (!hotspots.length) {
    return
  }

  autoHotspotTimer = window.setTimeout(() => {
    let hotspotIndex = 0
    activeHotspotIds.value = [hotspots[hotspotIndex].id]

    if (hotspots.length <= 1) return

    autoHotspotInterval = window.setInterval(() => {
      hotspotIndex = (hotspotIndex + 1) % hotspots.length
      activeHotspotIds.value = hotspots.slice(0, hotspotIndex + 1).map((hotspot) => hotspot.id)

      if (hotspotIndex === hotspots.length - 1) {
        window.clearInterval(autoHotspotInterval)
        autoHotspotInterval = null
      }
    }, AUTO_HOTSPOT_INTERVAL)
  }, AUTO_HOTSPOT_DELAY)
}

const startVideo = () => {
  videoPlaying.value = true
  nextTick(() => {
    videoRef.value?.play()
  })
}

const updateStepStageViewport = (stepKey) => {
  const stageElement = stepStageRefs[stepKey]
  if (!stageElement) return

  const nextViewport = {
    width: stageElement.clientWidth,
    height: stageElement.clientHeight
  }

  if (!nextViewport.width || !nextViewport.height) return

  const currentViewport = stepStageViewportMap.value[stepKey]
  if (currentViewport?.width === nextViewport.width && currentViewport?.height === nextViewport.height) {
    return
  }

  stepStageViewportMap.value = {
    ...stepStageViewportMap.value,
    [stepKey]: nextViewport
  }
}

const updateAllStepStageViewports = () => {
  Object.keys(stepStageRefs).forEach((stepKey) => {
    updateStepStageViewport(stepKey)
  })
}

const setStepImageSize = (event, stepKey) => {
  const imageElement = event.target
  if (!imageElement?.naturalWidth || !imageElement?.naturalHeight) return

  const nextSize = {
    width: imageElement.naturalWidth,
    height: imageElement.naturalHeight
  }

  const currentSize = stepImageSizeMap.value[stepKey]
  if (currentSize?.width === nextSize.width && currentSize?.height === nextSize.height) {
    updateStepStageViewport(stepKey)
    return
  }

  stepImageSizeMap.value = {
    ...stepImageSizeMap.value,
    [stepKey]: nextSize
  }

  updateStepStageViewport(stepKey)
}

const getHotspotStyle = (stepKey, hotspot) => {
  const stageViewport = stepStageViewportMap.value[stepKey]
  const imageSize = stepImageSizeMap.value[stepKey]

  if (!stageViewport || !imageSize) {
    return {
      left: `${hotspot.x}%`,
      top: `${hotspot.y}%`
    }
  }

  const scale = Math.max(
    stageViewport.width / imageSize.width,
    stageViewport.height / imageSize.height
  )
  const renderedWidth = imageSize.width * scale
  const renderedHeight = imageSize.height * scale
  const offsetLeft = (stageViewport.width - renderedWidth) / 2
  const offsetTop = (stageViewport.height - renderedHeight) / 2
  const left = offsetLeft + renderedWidth * (hotspot.x / 100)
  const top = offsetTop + renderedHeight * (hotspot.y / 100)

  return {
    left: `${left}px`,
    top: `${top}px`
  }
}

const getHotspotCenterPoint = (stepKey, hotspot) => {
  const hotspotStyle = getHotspotStyle(stepKey, hotspot)
  const stageViewport = stepStageViewportMap.value[stepKey]
  const parseAxis = (value, size, percentFallback) => {
    if (typeof value === 'string' && value.endsWith('px')) {
      return parseFloat(value)
    }
    if (!size) return null
    if (typeof value === 'string' && value.endsWith('%')) {
      return size * (parseFloat(value) / 100)
    }
    return size * (percentFallback / 100)
  }

  return {
    x: parseAxis(hotspotStyle.left, stageViewport?.width, hotspot.x),
    y: parseAxis(hotspotStyle.top, stageViewport?.height, hotspot.y),
    stageViewport
  }
}

const getBubbleLayout = (stepKey, hotspot) => {
  const { x: centerX, y: centerY, stageViewport } = getHotspotCenterPoint(stepKey, hotspot)
  const gap = hotspot.bubbleLeft ?? (isPortraitMobile.value ? 16 : HOTSPOT_BUBBLE_DEFAULT_LEFT)
  // 右侧至少能放下约两行短标签时优先朝右
  const preferRightMin = isPortraitMobile.value ? 108 : 140
  // 英文两行气泡大约高度，用于贴底时上推
  const estimatedBubbleHeight = isPortraitMobile.value ? 56 : 52

  if (centerX == null || !stageViewport?.width) {
    return {
      openLeft: hotspot.bubbleOpen === 'left',
      gap,
      maxWidth: null,
      top: hotspot.bubbleTop
    }
  }

  const spaceRight = stageViewport.width - centerX - gap - HOTSPOT_BUBBLE_STAGE_PAD
  const spaceLeft = centerX - gap - HOTSPOT_BUBBLE_STAGE_PAD

  let openLeft = false
  if (hotspot.bubbleOpen === 'left') {
    openLeft = true
  } else if (hotspot.bubbleOpen === 'right') {
    // 强制朝右，除非右侧几乎没有空间
    openLeft = spaceRight < 64 && spaceLeft > spaceRight
  } else {
    // auto：尽量朝右，右侧明显不够再朝左
    openLeft = spaceRight < preferRightMin && spaceLeft > spaceRight
  }

  const available = openLeft ? spaceLeft : spaceRight
  // 不能超过当前侧剩余空间，否则会被 step-stage 的 overflow:hidden 裁切
  const maxWidth = Math.min(HOTSPOT_BUBBLE_MAX_WIDTH, Math.max(available, 0))

  // 纵向：默认用配置 top；若气泡会超出 stage 底/顶，则夹紧到安全区内
  let top = hotspot.bubbleTop
  if (centerY != null && stageViewport.height) {
    const minTop = HOTSPOT_BUBBLE_STAGE_PAD - centerY
    const maxTop = stageViewport.height - HOTSPOT_BUBBLE_STAGE_PAD - centerY - estimatedBubbleHeight
    if (maxTop < minTop) {
      // stage 过矮时尽量贴上沿
      top = minTop
    } else {
      top = Math.min(Math.max(top, minTop), maxTop)
    }
  }

  return {
    openLeft,
    gap,
    maxWidth,
    top
  }
}

const getBubbleStyle = (stepKey, hotspot) => {
  const { openLeft, gap, maxWidth, top } = getBubbleLayout(stepKey, hotspot)
  const style = {
    top: `${top}px`,
    borderRadius: `${hotspot.bubbleRadius}px`
  }

  if (maxWidth != null) {
    style.maxWidth = `${maxWidth}px`
  }

  if (openLeft) {
    style.left = 'auto'
    style.right = `${gap}px`
    return style
  }

  style.left = `${gap}px`
  style.right = 'auto'
  return style
}

onMounted(() => {
  nextTick(() => {
    updateIsPortraitMobile()
    lastWindowScrollY.value = window.scrollY || window.pageYOffset || 0
    updateAllStepStageViewports()
    updateActiveWorkflowStage()
    updateScrollSnapState()
  })
  window.addEventListener('scroll', updateActiveWorkflowStage, { passive: true })
  window.addEventListener('scroll', updateScrollSnapState, { passive: true })
  window.addEventListener('resize', updateIsPortraitMobile, { passive: true })
  window.addEventListener('resize', updateActiveWorkflowStage, { passive: true })
  window.addEventListener('resize', updateScrollSnapState, { passive: true })
  window.addEventListener('resize', updateAllStepStageViewports, { passive: true })
})

onBeforeUnmount(() => {
  clearAutoHotspots()
  window.removeEventListener('scroll', updateActiveWorkflowStage)
  window.removeEventListener('scroll', updateScrollSnapState)
  window.removeEventListener('resize', updateIsPortraitMobile)
  window.removeEventListener('resize', updateActiveWorkflowStage)
  window.removeEventListener('resize', updateScrollSnapState)
  window.removeEventListener('resize', updateAllStepStageViewports)
  document.documentElement.classList.remove('air-ground-snap-active')
  document.body.classList.remove('air-ground-snap-active')
})

const updateScrollSnapState = () => {
  const root = timelineRootRef.value
  if (!root) return

  const rect = root.getBoundingClientRect()
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight
  const shouldEnableSnap = rect.top <= 0 && rect.bottom > viewportHeight

  document.documentElement.classList.toggle('air-ground-snap-active', shouldEnableSnap)
  document.body.classList.toggle('air-ground-snap-active', shouldEnableSnap)
}
</script>

<style scoped>
:global(html),
:global(body) {
  scroll-behavior: smooth;
}

:global(html.air-ground-snap-active),
:global(body.air-ground-snap-active) {
  scroll-snap-type: y mandatory;
}

.air-ground-page {
  width: 100%;
  background: var(--air-ground-color-bg);
  color: var(--air-ground-color-text);
  overflow-x: clip;
  --air-ground-color-bg: #e8e7ed;
  --air-ground-color-text: var(--pixso-color-surface);
  --air-ground-color-muted: rgba(0, 0, 0, 0.6);
  --air-ground-color-panel: rgba(232, 231, 237, 0.34);
  --air-ground-color-track: rgba(165, 165, 165, 0.58);
  --air-ground-color-track-active: rgba(22, 22, 23, 0.72);
  --air-ground-color-workflow-text: rgba(22, 22, 23, 0.52);
  --air-ground-color-workflow-dot-border: rgba(165, 165, 165, 0.7);
  --air-ground-color-workflow-dot-idle: rgba(165, 165, 165, 1);
  --air-ground-color-workflow-dot-active: rgba(22, 22, 23, 1);
  --air-ground-color-workflow-dot-done: rgba(255, 255, 255, 1);
  --air-ground-color-workflow-icon-light: rgba(255, 255, 255, 1);
  --air-ground-color-workflow-icon-dark: rgba(0, 0, 0, 1);
  --air-ground-color-step-desc: rgba(255, 255, 255, 0.86);
  --air-ground-color-step-fill: var(--air-ground-color-bg);
  --air-ground-section-title-accent: var(--pixso-color-primary);
  --air-ground-shadow-panel: 0 24px 80px rgba(0, 0, 0, 0.16);
  --air-ground-shadow-workflow: 0 24px 80px rgba(0, 0, 0, 0.22);
  --air-ground-hero-mask: linear-gradient(180deg, rgba(0, 0, 0, 0.08), rgba(0, 0, 0, 0.18));
  --air-ground-step-mask: linear-gradient(90deg, rgba(0, 0, 0, 0.58), rgba(0, 0, 0, 0.12) 52%, rgba(0, 0, 0, 0.34));
  --air-ground-stage: min(100%, var(--pixso-page-max));
  --air-ground-container: min(1200px, calc(100% - var(--pixso-page-padding) * 2));
  --air-ground-hero-height: min(1080px, calc(100vh - var(--pixso-nav-height)));
  --air-ground-hero-min: var(--pixso-hero-min);
  --air-ground-overview-top: 96px;
  --air-ground-overview-bottom: var(--pixso-section-gap-xl);
  --air-ground-ordering-bottom: var(--pixso-section-gap-xl);
  --air-ground-media-width: 960px;
  --air-ground-order-phone-width: 420px;
  --air-ground-timeline-height: 100vh;
  --air-ground-timeline-height-tablet: 100vh;
  --air-ground-timeline-height-mobile: 100vh;
  --air-ground-workflow-width: 1008px;
  --air-ground-workflow-height: 165px;
  --air-ground-workflow-height-mobile: 132px;
  --air-ground-workflow-cell-min: 92px;
  --air-ground-workflow-cell-tablet: 120px;
  --air-ground-workflow-cell-mobile: 108px;
  --air-ground-workflow-count: 8;
  --air-ground-workflow-radius: var(--pixso-radius-lg);
  --air-ground-workflow-sticky-top: calc(var(--pixso-nav-height) + var(--pixso-gap-lg));
  --air-ground-workflow-pad-y: var(--pixso-gap-md);
  --air-ground-workflow-pad-x: calc(var(--pixso-gap-md) + var(--pixso-gap-sm) - 2px);
  --air-ground-workflow-content-pad-top: calc(var(--pixso-gap-md) - 2px);
  --air-ground-workflow-track-height: 2px;
  --air-ground-workflow-dot-size: 56px;
  --air-ground-workflow-dot-border: 2px;
  --air-ground-workflow-icon-size: 28px;
  --air-ground-workflow-content-gap: var(--pixso-gap-sm);
  --air-ground-workflow-label-height: var(--pixso-lh-14);
  /* Track is drawn only on the ol; top is measured from the ol padding box so base + progress share one coordinate. */
  --air-ground-workflow-track-top: calc(
    var(--air-ground-workflow-pad-y)
    + var(--air-ground-workflow-content-pad-top)
    + var(--air-ground-workflow-dot-size) / 2
    - var(--air-ground-workflow-track-height) / 2
  );
  --air-ground-workflow-track-inset: calc(
    var(--air-ground-workflow-pad-x)
    + (100% - 2 * var(--air-ground-workflow-pad-x)) / var(--air-ground-workflow-count) / 2
  );
  --air-ground-workflow-track-span: calc(
    (100% - 2 * var(--air-ground-workflow-pad-x)) * (1 - 1 / var(--air-ground-workflow-count))
  );
  --air-ground-workflow-blur: 18px;
  --air-ground-step-title-max: 780px;
  --air-ground-step-desc-max: 760px;
}

.air-ground-page,
.air-ground-page * {
  box-sizing: border-box;
}

.air-ground-page__hero {
  width: 100vw;
  margin-left: calc(50% - 50vw);
  height: var(--air-ground-hero-height);
  min-height: var(--air-ground-hero-min);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  position: relative;
  overflow: hidden;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
}

.air-ground-page__hero::after {
  content: "";
  position: absolute;
  inset: 0;
  z-index: 1;
  background: var(--air-ground-hero-mask);
}

.air-ground-page__hero-copy {
  position: relative;
  z-index: 2;
  width: var(--air-ground-container);
  margin-top: 120px;
  text-align: center;
}

.air-ground-page__hero-title {
  margin: 0;
  color: var(--pixso-color-text);
  font-size: var(--pixso-fs-96);
  font-weight: 700;
  line-height: var(--pixso-lh-96);
}

.air-ground-page__overview {
  width: var(--air-ground-container);
  margin: 0 auto;
  padding: var(--air-ground-overview-top) 0 var(--air-ground-overview-bottom);
  text-align: center;
}

.air-ground-page__eyebrow {
  margin: 0;
  color: var(--air-ground-color-text);
  font-family: var(--pixso-font-en);
  font-size: var(--pixso-fs-28);
  font-style: italic;
  line-height: var(--pixso-lh-28);
}

.air-ground-page__section-title {
  margin: var(--pixso-gap-sm) 0 0;
  font-size: var(--pixso-fs-96);
  font-weight: 700;
  line-height: var(--pixso-lh-96);
}

.air-ground-page__overview-desc,
.air-ground-page__body {
  margin: var(--pixso-gap-md) auto 0;
  max-width: var(--air-ground-media-width);
  color: var(--air-ground-color-muted);
  font-size: var(--pixso-fs-20);
  font-weight: 700;
  line-height: var(--pixso-lh-20);
}



.air-ground-page__video-bg {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.air-ground-page__video-text-overlay {
  position: absolute;
  top: 12%;
  left: 6%;
  right: 6%;
  z-index: 2;
  pointer-events: none;
}

.air-ground-page__video-slogan {
  font-size: var(--pixso-fs-32);
  font-weight: 600;
  line-height: var(--pixso-lh-32);
  color: rgba(255, 255, 255, 0.85);
  margin-bottom: 8px;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
  letter-spacing: -0.3px;
}

.air-ground-page__video-subtitle {
  font-size: var(--pixso-fs-24);
  font-weight: 700;
  line-height: var(--pixso-lh-24);
  color: #4da3ff;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
  letter-spacing: -0.2px;
}

.air-ground-page__media {
  position: relative;
  margin: 0;
  overflow: hidden;
}

.air-ground-page__video-cover {
  position: absolute;
  inset: 0;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  z-index: 1;
}

.air-ground-page__video-poster {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  object-position: center;
}

.air-ground-page__video-play {
  position: absolute;
  left: 50%;
  top: 50%;
  width: clamp(88px, 10vw, 160px);
  height: clamp(88px, 10vw, 160px);
  transform: translate(-50%, -50%);
  pointer-events: none;
}

.air-ground-page__media--video {
  width: min(100%, var(--air-ground-media-width));
  aspect-ratio: 16 / 9;
  margin: var(--pixso-gap-lg) auto 0;
  border-radius: 20px;
  box-shadow: var(--air-ground-shadow-panel);
}

.air-ground-page__media video {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  object-position: center;
}

.air-ground-page__ordering {
  position: relative;
  z-index: 2;
  width: var(--air-ground-container);
  margin: 0 auto;
  padding: 0 0 var(--air-ground-ordering-bottom);
  display: grid;
  grid-template-columns: minmax(0, 1fr) var(--air-ground-order-phone-width);
  align-items: center;
  gap: calc(var(--pixso-section-gap-md) + var(--pixso-gap-lg));
}

.air-ground-page__ordering-copy {
  text-align: left;
}

.air-ground-page__ordering-copy .air-ground-page__overview-desc,
.air-ground-page__ordering-copy .air-ground-page__body {
  margin-left: 0;
}

.air-ground-page__phone {
  display: block;
  width: 100%;
  aspect-ratio: 50 / 31;
  border-radius: var(--pixso-radius-lg);
  box-shadow: var(--air-ground-shadow-panel);
  object-fit: cover;
}

.air-ground-page__timeline {
  width: 100%;
  position: relative;
  z-index: 1;
}

.air-ground-page__workflow-shell {
  position: fixed;
  left: 50%;
  bottom: 20px;
  z-index: 4;
  width: min(calc(100% - var(--pixso-page-padding) * 2), var(--air-ground-workflow-width));
  transform: translateX(-50%);
  opacity: 0;
  pointer-events: none;
}

.air-ground-page__workflow-shell.is-visible {
  opacity: 1;
  pointer-events: auto;
}

.air-ground-page__step {
  position: relative;
  width: 100%;
  min-height: var(--air-ground-timeline-height);
  overflow: hidden;
  isolation: isolate;
  scroll-snap-align: start;
  scroll-snap-stop: always;
  background: #050608;
}

.air-ground-page__step-media {
  position: absolute;
  inset: 0;
  z-index: 0;
}

.air-ground-page__step-stage {
  position: absolute;
  inset: 0;
  z-index: 1;
  overflow: hidden;
}

.air-ground-page__step-mobile-heading {
  display: none;
}

.air-ground-page__step-image {
  position: absolute;
  inset: 0;
  z-index: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.air-ground-page__step-mask {
  position: absolute;
  inset: 0;
  z-index: 1;
  background: var(--air-ground-step-mask);
}

.air-ground-page__step-hotspots {
  position: absolute;
  inset: 0;
  z-index: 2;
  pointer-events: none;
}

.air-ground-page__hotspot {
  position: absolute;
  transform: translate(-50%, -50%);
  pointer-events: auto;
}

.air-ground-page__hotspot-bubble {
  position: absolute;
  opacity: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: max-content;
  max-width: min(280px, calc(100vw - 48px));
  min-height: 46px;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.14);
  transform: translateY(4px);
  transition: opacity 0.2s ease, transform 0.2s ease;
  pointer-events: none;
}

.air-ground-page__hotspot.is-active .air-ground-page__hotspot-bubble,
.air-ground-page__hotspot:hover .air-ground-page__hotspot-bubble,
.air-ground-page__hotspot:focus-within .air-ground-page__hotspot-bubble {
  opacity: 1;
  transform: translateY(0);
}

.air-ground-page__hotspot-bubble-text {
  color: #161617;
  font-size: var(--pixso-fs-14);
  font-weight: 700;
  line-height: var(--pixso-lh-14);
  text-align: center;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  hyphens: auto;
}

/* 英文气泡文案更长，整体略缩小，避免显得臃肿 */
.air-ground-page--en .air-ground-page__hotspot-bubble-text {
  font-size: var(--pixso-fs-12);
  font-weight: 500;
  line-height: var(--pixso-lh-12);
}

.air-ground-page__hotspot-preview {
  position: absolute;
  opacity: 0;
  overflow: hidden;
  max-width: min(260px, calc(100vw - 64px));
  border-radius: 20px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.2);
  transform: translateX(28px);
  transition: opacity 0.24s ease, transform 0.24s ease;
  pointer-events: none;
}

.air-ground-page__hotspot-preview.is-active {
  opacity: 1;
  transform: translateX(0);
}

.air-ground-page__hotspot-preview-image {
  display: block;
  width: 100%;
  height: auto;
}

.air-ground-page__hotspot-pulse {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: rgba(98, 224, 136, 0.2);
  transform: translate(-50%, -50%);
  animation: air-ground-hotspot-pulse 2.2s ease-out infinite;
  pointer-events: none;
}

.air-ground-page__hotspot-trigger {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 30px;
  height: 30px;
  padding: 0;
  appearance: none;
  border: 1px solid rgba(98, 224, 136, 0.9);
  border-radius: 50%;
  background: rgba(98, 224, 136, 0.18);
  cursor: pointer;
  transform: translate(-50%, -50%);
  transition: transform 0.2s ease, background 0.2s ease, border-color 0.2s ease;
}

.air-ground-page__hotspot-trigger:focus {
  outline: none;
}

.air-ground-page__hotspot.is-active .air-ground-page__hotspot-trigger,
.air-ground-page__hotspot:hover .air-ground-page__hotspot-trigger,
.air-ground-page__hotspot:focus-within .air-ground-page__hotspot-trigger {
  background: rgba(98, 224, 136, 0.28);
  border-color: rgba(98, 224, 136, 1);
}

.air-ground-page__hotspot-core {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #62e088;
  transform: translate(-50%, -50%);
  filter: drop-shadow(0 0 6px rgba(98, 224, 136, 0.65));
  animation: air-ground-hotspot-glow 1.4s ease-in-out infinite;
  pointer-events: none;
}

@keyframes air-ground-hotspot-pulse {
  0% {
    transform: translate(-50%, -50%) scale(0.78);
    opacity: 0.9;
  }

  70% {
    transform: translate(-50%, -50%) scale(1.45);
    opacity: 0;
  }

  100% {
    transform: translate(-50%, -50%) scale(1.45);
    opacity: 0;
  }
}

@keyframes air-ground-hotspot-glow {
  0%,
  100% {
    transform: translate(-50%, -50%) scale(0.92);
    opacity: 0.82;
    filter: drop-shadow(0 0 6px rgba(98, 224, 136, 0.55));
  }

  50% {
    transform: translate(-50%, -50%) scale(1.12);
    opacity: 1;
    filter: drop-shadow(0 0 12px rgba(98, 224, 136, 0.95));
  }
}

.air-ground-page__step-copy {
  position: absolute;
  z-index: 2;
  width: var(--air-ground-container);
  min-height: var(--air-ground-timeline-height);
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-start;
  padding-bottom: 160px;
  color: var(--pixso-color-text);
}

.air-ground-page__workflow {
  position: relative;
  width: 100%;
  max-width: 100%;
  min-height: var(--air-ground-workflow-height);
  margin: 0 auto;
  padding: var(--air-ground-workflow-pad-y) var(--air-ground-workflow-pad-x);
  display: grid;
  grid-template-columns: repeat(var(--air-ground-workflow-count), minmax(var(--air-ground-workflow-cell-min), 1fr));
  gap: 0;
  align-items: stretch;
  border-radius: var(--air-ground-workflow-radius);
  background: var(--air-ground-color-panel);
  box-shadow: var(--air-ground-shadow-workflow);
  -webkit-backdrop-filter: blur(var(--air-ground-workflow-blur));
  backdrop-filter: blur(var(--air-ground-workflow-blur));
  list-style: none;
  align-self: center;
  pointer-events: auto;
}

/* Base track + progress share the same geometry on the ol so they cannot double-render with a 1px offset. */
.air-ground-page__workflow::before,
.air-ground-page__workflow::after {
  content: "";
  position: absolute;
  z-index: 1;
  left: var(--air-ground-workflow-track-inset);
  top: var(--air-ground-workflow-track-top);
  height: var(--air-ground-workflow-track-height);
  pointer-events: none;
}

.air-ground-page__workflow::before {
  width: var(--air-ground-workflow-track-span);
  background: var(--air-ground-color-track);
}

.air-ground-page__workflow::after {
  width: calc(var(--air-ground-workflow-track-span) * var(--air-ground-workflow-progress, 0));
  background: var(--air-ground-color-track-active);
  transition: width 0.08s linear;
}

.air-ground-page__workflow-item {
  position: relative;
  z-index: 2;
  min-width: 0;
  min-height: 100%;
  padding: 0 var(--pixso-gap-xs);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  color: var(--air-ground-color-workflow-text);
  font-size: var(--pixso-fs-14);
  font-weight: 400;
  line-height: var(--pixso-lh-14);
  text-align: center;
}

.air-ground-page__workflow-content {
  position: relative;
  z-index: 2;
  width: 100%;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  margin: 0;
  padding-top: var(--air-ground-workflow-content-pad-top);
  gap: var(--air-ground-workflow-content-gap);
  border: 0;
  background: transparent;
  appearance: none;
  color: inherit;
  font: inherit;
  text-align: inherit;
  cursor: pointer;
}

.air-ground-page__workflow-content:focus-visible {
  outline: 2px solid var(--air-ground-color-workflow-dot-active);
  outline-offset: 4px;
  border-radius: 16px;
}

.air-ground-page__workflow-dot {
  position: relative;
  width: var(--air-ground-workflow-dot-size);
  height: var(--air-ground-workflow-dot-size);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: var(--air-ground-workflow-dot-border) solid var(--air-ground-color-workflow-dot-border);
  background: var(--air-ground-color-workflow-dot-idle);
  transition: background-color 0.24s ease, border-color 0.24s ease;
}

.air-ground-page__workflow-icon {
  width: var(--air-ground-workflow-icon-size);
  height: var(--air-ground-workflow-icon-size);
  display: block;
  background-color: var(--air-ground-color-workflow-icon-light);
  -webkit-mask-image: var(--air-ground-workflow-icon);
  -webkit-mask-repeat: no-repeat;
  -webkit-mask-position: center;
  -webkit-mask-size: contain;
  mask-image: var(--air-ground-workflow-icon);
  mask-repeat: no-repeat;
  mask-position: center;
  mask-size: contain;
  transition: background-color 0.24s ease;
}

.air-ground-page__workflow-item.is-active .air-ground-page__workflow-dot {
  border-color: var(--air-ground-color-workflow-dot-active);
  background: var(--air-ground-color-workflow-dot-active);
}

.air-ground-page__workflow-item.is-done .air-ground-page__workflow-dot {
  border-color: var(--air-ground-color-workflow-dot-done);
  background: var(--air-ground-color-workflow-dot-done);
}

.air-ground-page__workflow-item.is-done .air-ground-page__workflow-icon {
  background-color: var(--air-ground-color-workflow-icon-dark);
}

.air-ground-page__workflow-item.is-active {
  color: var(--air-ground-color-text);
  font-weight: 700;
}

.air-ground-page__workflow-label {
  width: 100%;
  min-height: calc(var(--air-ground-workflow-label-height) * 2 + 4px);
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  word-break: break-word;
}

.air-ground-page__step-title {
  margin: 0;
  max-width: var(--air-ground-step-title-max);
  color: var(--pixso-color-text);
  font-size: var(--pixso-fs-96);
  font-weight: 700;
  line-height: var(--pixso-lh-96);
}

.air-ground-page__step-desc {
  margin: var(--pixso-gap-md) 0 0;
  max-width: var(--air-ground-step-desc-max);
  color: var(--air-ground-color-step-desc);
  font-size: var(--pixso-fs-24);
  font-weight: 700;
  line-height: var(--pixso-lh-24);
}

@media (max-width: 1200px) {
  .air-ground-page {
    --air-ground-overview-bottom: 180px;
    --air-ground-ordering-bottom: 180px;
  }

  .air-ground-page__hero {
    height: var(--pixso-hero-height);
    min-height: var(--air-ground-hero-min);
  }

  .air-ground-page__step,
  .air-ground-page__step-copy {
    --air-ground-timeline-height: var(--air-ground-timeline-height-tablet);
  }

  .air-ground-page__hero-title,
  .air-ground-page__section-title,
  .air-ground-page__step-title {
    font-size: var(--pixso-fs-72);
    line-height: var(--pixso-lh-72);
  }

  .air-ground-page {
    --air-ground-workflow-pad-x: calc(var(--pixso-gap-md) + var(--pixso-gap-xs));
  }

  .air-ground-page__workflow {
    overflow-x: auto;
    grid-template-columns: repeat(var(--air-ground-workflow-count), var(--air-ground-workflow-cell-tablet));
  }

  .air-ground-page__ordering {
    grid-template-columns: 1fr;
    gap: var(--pixso-gap-lg);
  }

  .air-ground-page__phone {
    width: min(100%, var(--air-ground-order-phone-width));
    margin: 0 auto;
  }
}

@media (max-width: 1200px), (max-height: 500px) and (orientation: landscape) {
  .air-ground-page {
    --air-ground-container: calc(100% - var(--pixso-page-padding) * 2);
    --air-ground-overview-top: calc(var(--pixso-gap-lg) + var(--pixso-gap-sm) - var(--pixso-gap-xs));
    --air-ground-overview-bottom: calc(var(--pixso-gap-lg) + var(--pixso-gap-md));
    --air-ground-ordering-bottom: calc(var(--pixso-gap-lg) + var(--pixso-gap-md));
    --air-ground-workflow-sticky-top: calc(var(--pixso-nav-height) + var(--pixso-gap-sm));
    --air-ground-workflow-width: calc(100% - var(--pixso-page-padding) * 2);
    --air-ground-workflow-height-mobile: 118px;
    --air-ground-workflow-cell-mobile: minmax(0, 1fr);
    --air-ground-workflow-pad-y: 12px;
    --air-ground-workflow-pad-x: 8px;
    --air-ground-workflow-content-pad-top: 0px;
    --air-ground-workflow-dot-size: 34px;
    --air-ground-workflow-icon-size: 16px;
    --air-ground-workflow-content-gap: 6px;
    --air-ground-workflow-label-height: 14px;
    --air-ground-workflow-track-height: 1px;
  }

  .air-ground-page__hero {
    height: var(--pixso-hero-mobile);
    min-height: var(--pixso-hero-mobile);
  }

  .air-ground-page__hero-title,
  .air-ground-page__section-title,
  .air-ground-page__step-title {
    font-size: clamp(var(--pixso-fs-36), 11.28vw, var(--pixso-fs-48));
    line-height: clamp(var(--pixso-lh-36), 12.82vw, var(--pixso-lh-48));
  }

  .air-ground-page__overview {
    padding: var(--air-ground-overview-top) 0 var(--air-ground-overview-bottom);
  }

  .air-ground-page__eyebrow {
    font-size: var(--pixso-fs-20);
    line-height: var(--pixso-lh-20);
  }

  .air-ground-page__overview-desc,
  .air-ground-page__body,
  .air-ground-page__step-desc {
    font-size: var(--pixso-fs-16);
    line-height: var(--pixso-lh-16);
  }

  .air-ground-page__media--video {
    margin-top: calc(var(--pixso-gap-md) + var(--pixso-gap-sm) - var(--pixso-gap-xs));
    border-radius: var(--pixso-radius-md);
  }

  .air-ground-page__ordering {
    padding-bottom: var(--air-ground-ordering-bottom);
  }

  .air-ground-page__step,
  .air-ground-page__step-copy {
    --air-ground-timeline-height: var(--air-ground-timeline-height-mobile);
  }

  .air-ground-page__step-copy {
    padding: var(--air-ground-ordering-bottom) 0;
  }

  .air-ground-page__workflow-shell {
    left: 50%;
    bottom: 12px;
    width: var(--air-ground-workflow-width);
    max-width: 100%;
    transform: translateX(-50%);
  }

  .air-ground-page__workflow {
    min-height: var(--air-ground-workflow-height-mobile);
    grid-template-columns: repeat(var(--air-ground-workflow-count), var(--air-ground-workflow-cell-mobile));
    border-radius: var(--pixso-radius-md);
    overflow-x: visible;
  }

  .air-ground-page__hotspot-preview {
    max-width: min(176px, calc(100vw - 120px));
    border-radius: 16px;
  }

  .air-ground-page__workflow-item {
    padding: 0 2px;
    font-size: var(--pixso-fs-10);
    line-height: var(--pixso-lh-10);
  }

  .air-ground-page__workflow-label {
    min-height: calc(var(--air-ground-workflow-label-height) * 2);
  }
}

@media (max-width: 1200px) and (orientation: portrait) {
  .air-ground-page {
    --air-ground-overview-bottom: 56px;
    --air-ground-timeline-height-mobile: auto;
  }

  .air-ground-page__timeline {
    width: var(--air-ground-container);
    margin: 0 auto;
    padding: 0 0 56px;
    display: flex;
    flex-direction: column;
    gap: 28px;
  }

  .air-ground-page__workflow-shell {
    display: none;
  }

  .air-ground-page__step {
    min-height: auto;
    overflow: visible;
    background: transparent;
  }

  .air-ground-page__step-media {
    position: relative;
    inset: auto;
  }

  .air-ground-page__step-stage {
    position: relative;
    inset: auto;
    min-height: 0;
    border-radius: 16px;
    background: linear-gradient(180deg, #fcfcfd 0%, #f1f1f6 100%);
    box-shadow: 0 24px 80px rgba(28, 34, 44, 0.08);
  }

  .air-ground-page__step-mobile-heading {
    position: absolute;
    top: 28px;
    left: 20px;
    right: 20px;
    z-index: 4;
    display: flex;
    align-items: baseline;
    gap: 0;
    color: #0f1115;
    font-size: var(--pixso-fs-18);
    font-weight: 800;
    line-height: var(--pixso-lh-18);
    letter-spacing: -0.02em;
  }

  .air-ground-page__step-mobile-index {
    margin-right: 2px;
  }

  .air-ground-page__step-image {
    position: relative;
    inset: auto;
    display: block;
    height: auto;
    object-position: center;
    object-fit: contain;
  }

  .air-ground-page__step-mask {
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0) 28%, rgba(255, 255, 255, 0) 100%);
  }

  .air-ground-page__step-hotspots {
    z-index: 5;
  }

  .air-ground-page__hotspot-bubble {
    opacity: 1;
    min-height: 42px;
    padding: 10px 16px;
    border-radius: 999px !important;
    box-shadow: 0 10px 32px rgba(24, 29, 37, 0.12);
    transform: none;
    /* 父级热点近乎 0 宽，不能用百分比；最终 max-width 由 getBubbleStyle 按位置收紧 */
    max-width: min(220px, calc(100vw - 64px));
  }

  .air-ground-page__hotspot-bubble-text {
    font-size: var(--pixso-fs-10);
    line-height: var(--pixso-lh-10);
  }

  .air-ground-page--en .air-ground-page__hotspot-bubble {
    min-height: 36px;
    padding: 8px 12px;
    max-width: min(200px, calc(100vw - 72px));
  }

  .air-ground-page--en .air-ground-page__hotspot-bubble-text {
    font-size: var(--pixso-fs-10);
    font-weight: 500;
    line-height: var(--pixso-lh-10);
  }

  .air-ground-page__hotspot-preview {
    right: -10px !important;
    max-width: min(108px, calc(100vw - 176px));
    border-radius: 18px;
    transform: none;
    box-shadow: none;
  }

  .air-ground-page__hotspot-preview-image {
    width: 50%;
    margin: 0 auto;
  }

  .air-ground-page__hotspot-pulse {
    width: 20px;
    height: 20px;
    background: rgba(98, 224, 136, 0.18);
  }

  .air-ground-page__hotspot-trigger {
    width: 18px;
    height: 18px;
    border: 0;
    background: rgba(98, 224, 136, 0.24);
  }

  .air-ground-page__hotspot-core {
    width: 12px;
    height: 12px;
  }
}

@media (max-height: 500px) and (orientation: landscape) {
  .air-ground-page {
    --air-ground-timeline-height-mobile: 100svh;
    --air-ground-workflow-height-mobile: 88px;
    --air-ground-workflow-pad-y: 8px;
    --air-ground-workflow-pad-x: 10px;
    --air-ground-workflow-content-pad-top: 0px;
    --air-ground-workflow-dot-size: 28px;
    --air-ground-workflow-icon-size: 14px;
    --air-ground-workflow-content-gap: 4px;
    --air-ground-workflow-label-height: 12px;
    /* Items are vertically centered in landscape; keep track on the ol only (still a single coordinate). */
    --air-ground-workflow-track-top: calc(
      50%
      - (var(--air-ground-workflow-dot-size) + var(--air-ground-workflow-content-gap) + var(--air-ground-workflow-label-height) * 2) / 2
      + var(--air-ground-workflow-dot-size) / 2
      - var(--air-ground-workflow-track-height) / 2
    );
  }

  .air-ground-page__workflow-shell {
    bottom: 12px;
    top: auto;
    transform: translateX(-50%);
  }

  .air-ground-page__workflow {
    min-height: var(--air-ground-workflow-height-mobile);
    align-items: center;
  }

  .air-ground-page__workflow-item {
    display: flex;
    align-items: center;
  }

  .air-ground-page__workflow-content {
    min-height: auto;
    justify-content: center;
    gap: var(--air-ground-workflow-content-gap);
  }

  .air-ground-page__workflow-label {
    min-height: 24px;
    display: flex;
    align-items: flex-start;
    justify-content: center;
  }
}
</style>
